<template>
    <div :id="tableId" class="table-responsive">
        <table class="table table-hover" style="table-layout: fixed; font-size: 0.875em">
            <thead>
                <tr>
                    <th v-for="field in fields" style="width: 10em">
                        {{ $t(field.header) }}
                    </th>
                    <th v-if="isEditable" id="lastColumn" style="width: 9em;"></th>
                </tr>
            </thead>
            <tbody>
                <tr v-for="(item, index) in items" :key="index" @keyup.enter="updateItem(item, fields)" @keyup.esc="revertChanges(item)" @click="routeTo(item)" :class="cursorType">
                    <td v-for="(field,index) in fields" :key="index+'field'">
                        <div class="tableBodyCell">
                            <template v-if="field.type === 'number' || field.type === 'float'">
                                <template v-if="field.editable && item.editMode && isEditable">
                                    <masked-input
                                        v-model="item[field.key].editedValue"
                                        :keepCharPositions="false" type="text"
                                        :mask="field.type === 'number' ? maskOfNumber : maskOfCurrency"
                                        :placeholder="$t(field.key)"
                                        :class="Object.assign(item[field.key].classes, {'masked-input-custom': true})"
                                        @focus="resetWarning(item[field.key])">
                                    </masked-input>
                                </template>
                                <template v-else-if="!isNil(item[field.key].value)">
                                    {{item[field.key].value}}
                                </template>
                                <template v-else>
                                    {{ item[field.key].defaultValue }}
                                </template>
                            </template>
                            <template v-else-if="field.type === 'dropdown'">
                                <template v-if="field.editable && item.editMode && isEditable">
                                    <b-form-select
                                        v-model="item[field.key].editedValue"
                                        style="border-radius: unset !important; height: 100%;"
                                        :class="Object.assign(item[field.key].classes, {'form-control-custom': true})"
                                        @focus.native="resetWarning(item[field.key])"
                                    >
                                        <option :value="null" disabled>{{$t('choose')}}</option>
                                        <option v-for="element in field.data" :value="element">{{ (element.name) ? element.name : element }}</option>
                                    </b-form-select>
                                </template>
                                <template v-else>
                                    {{ (item[field.key].value && item[field.key].value.name) ? item[field.key].value.name : item[field.key].value }}
                                </template>
                            </template>
                            <template v-else-if="field.type === 'date'">
                                <template v-if="field.editable && item.editMode && isEditable">
                                    <el-date-picker
                                        v-model="item[field.key].editedValue"
                                        type="date"
                                        format="dd/MM/yyyy"
                                        :readonly="!field.editable"
                                        @focus="resetWarning(item[field.key])"
                                        :placeholder="$t('chooseDatePlaceholder')"
                                        style="width: 100%"
                                        :class="item[field.key].classes">
                                    </el-date-picker>
                                </template>
                                <template v-else-if="!isNil(item[field.key].value)">
                                    {{item[field.key].value | formatDate('DD/MM/YYYY')}}
                                </template>
                                <template v-else>
                                    {{ item[field.key].defaultValue }}
                                </template>
                            </template>
                            <template v-else-if="field.type === 'daterange'">
                                <template v-if="field.editable && item.editMode && isEditable">
                                    <el-date-picker
                                        v-model="item[field.key].editedValue"
                                        type="daterange"
                                        :readonly="!field.editable"
                                        format="dd/MM/yyyy"
                                        @focus="resetWarning(item[field.key])"
                                        :placeholder="$t('chooseDatePlaceholder')"
                                        style="width: 100%"
                                        :class="item[field.key].classes"/>
                                </template>
                                <template v-else-if="!isNil(item[field.key].value)">
                                    {{item[field.key].value[0] | formatDate('DD/MM/YYYY')}} - {{item[field.key].value[1] | formatDate('DD/MM/YYYY')}}
                                </template>
                                <template v-else>
                                    {{ item[field.key].defaultValue }}
                                </template>
                            </template>
                            <template v-else-if="field.type === 'link'">
                                <router-link :to="item.path">{{$t(item[field.key].value)}}</router-link>
                            </template>
                            <template v-else-if="field.type === 'file'">
                                <template v-if="field.editable && item.editMode && isEditable">
                                    <div v-if="item[field.key] && item[field.key].editedFileId" @click="triggerPopover(item)">
                                        <upload :reactiveId="`a_${reactiveId}_${item.id}`" @ondownload="downloadFile(item[field.key].editedFileUrl||item[field.key].fileUrl)" @onremove="handleRemoving($event, item[field.key])" @onupload="handleUploading($event, item[field.key] = {})"/>
                                        <b-progress v-show="uploadFiles.length ? uploadFiles[0].active : false" :value="uploadFiles.length ? Number(uploadFiles[0].progress) : 100" variant="secondary" height="0.3em"/>
                                    </div>
                                    <upload-regular v-else @onupload="handleUploading($event, item[field.key] = {})"/>
                                </template>
                                <template v-else>
                                    <div class="file" v-if="item[field.key] && (item[field.key].editedFileId)" @click="downloadFile(item[field.key].fileUrl)">
                                        <span><i class="fas fa-file-alt fa-lg"></i></span>
                                    </div>
                                    <div class="file" v-else>
                                        <span>-</span>
                                    </div>
                                </template>
                            </template>
                            <template v-else-if="field.type === 'icon'">
                                <span v-if="item[field.key].value === 'health-ok'"><i style="color: #1c7430" class="fas fa-check"></i></span>
                                <span v-else><i class="fas fa-times" style="color: red;"></i></span>
                            </template>
                            <template v-else>
                                <template v-if="field.editable && item.editMode && isEditable">
                                    <b-form-input v-model="item[field.key].editedValue" :placeholder="field.header" :class="Object.assign(item[field.key].classes, {'masked-input-custom': true})"/>
                                </template>
                                <template v-else>
                                    {{item[field.key].value}}
                                </template>
                            </template>
                        </div>
                    </td>
                    <td v-if="isEditable" :style="{width: lastColumnWidth, 'margin-right': '4px'}" class="edit-column">
                        <div class="tableBodyCell">
                            <div v-if="!item.editMode" @click="makeRowEditable(item)" :key="'editspan'" style="color: #8888; cursor: pointer">
                                <i class="fas fa-edit"></i>
                            </div>
                            <div :key="'btngroup'" v-else class="table-button-group table-cell">
                                <b-button v-b-tooltip.hover :title="$t('save')" @click="updateItem(item, fields)"><i class="fas fa-check"></i></b-button>
                                <b-button v-b-tooltip.hover :title="$t('cancel')" @click="revertChanges(item)"><i class="fas fa-ban"></i></b-button>
                                <b-button v-b-modal.warn v-b-tooltip.hover :title="$t('delete')" @click="deleteItem($event, item.id)" variant="danger"><i class="fas fa-trash-alt"></i></b-button>
                            </div>
                        </div>
                    </td>
                </tr>
                <transition name="fadere" mode="out-in">
                    <tr v-if="insertMode" @keyup.enter="createItem(tempItem)" @keyup.esc="cancelNewRow()" style="border-bottom: 1px solid #ddd">
                        <td v-for="field in fields">
                            <div v-if="field.editable">
                                <template v-if="field.type === 'number' || field.type === 'float'">
                                    <masked-input
                                        v-model="tempItem[field.key].value"
                                        :keepCharPositions="false"
                                        :mask="field.type === 'number' ? maskOfNumber : maskOfCurrency"
                                        :placeholder="field.header"
                                        @focus="resetWarning(tempItem[field.key])"
                                        :class="Object.assign(tempItem[field.key].classes, {'masked-input-custom': true})"
                                        type="text"/>
                                </template>
                                <template v-else-if="field.type === 'dropdown'">
                                    <b-form-select
                                        v-model="tempItem[field.key].value"
                                        style="border-radius: unset !important; height: 100%;"
                                        :class="Object.assign(tempItem[field.key].classes, {'form-control-custom': true})"
                                        @focus.native="resetWarning(tempItem[field.key])"
                                    >
                                        <option :value="null" disabled>{{$t('choose')}}</option>
                                        <option v-for="element in field.data" :value="element">{{ (element.name) ? element.name : element }}</option>
                                    </b-form-select>
                                </template>
                                <template v-else-if="field.type === 'date'">
                                    <el-date-picker :placeholder="$t('chooseDatePlaceholder')"
                                                    v-model="tempItem[field.key].value"
                                                    type="date"
                                                    format="dd/MM/yyyy"
                                                    style="width: 100%"
                                                    :class="tempItem[field.key].classes"
                                                    @focus="resetWarning(tempItem[field.key])"/>
                                </template>
                                <template v-else-if="field.type === 'daterange'">
                                    <el-date-picker :placeholder="$t('chooseDatePlaceholder')"
                                                    v-model="tempItem[field.key].value"
                                                    format="dd/MM/yyyy"
                                                    type="daterange"
                                                    style="width: 100%"
                                                    :class="tempItem[field.key].classes"
                                                    @focus="resetWarning(tempItem[field.key])"/>
                                </template>
                                <template v-else-if="field.type === 'file'">
                                    <div v-if="tempItem[field.key] && tempItem[field.key].fileId">
                                        <upload :reactiveId="`a_${reactiveId}`" @ondownload="downloadFile(tempItem[field.key].fileUrl)" @onremove="handleTempRemoving($event, tempItem[field.key])" @onupload="handleTempUploading($event, tempItem[field.key])"/>
                                        <b-progress v-show="uploadFiles.length ? uploadFiles[0].active : false" :value="uploadFiles.length ? Number(uploadFiles[0].progress) : 100" variant="secondary" height="0.3em"/>
                                    </div>
                                    <upload-regular v-else @onupload="handleTempUploading($event, tempItem[field.key])"/>
                                </template>
                                <template v-else>
                                    <b-form-input
                                        v-model="tempItem[field.key].value"
                                        :placeholder="field.header"
                                        :class="Object.assign(tempItem[field.key].classes, {'masked-input-custom': true})"
                                        @focus.native="resetWarning(tempItem[field.key])"/>
                                </template>
                            </div>
                        </td>
                        <td v-if="isEditable" class="edit-column" :style="{width: lastColumnWidth, 'margin-right': '4px'}">
                            <div class="table-button-group">
                                <b-button v-b-tooltip.hover :title="$t('save')" @click="createItem(tempItem)" style="mso-border-shadow: no"><i class="fas fa-check"></i></b-button>
                                <b-button v-b-tooltip.hover :title="$t('cancel')" @click="cancelNewRow()"><i class="fas fa-ban"></i></b-button>
                            </div>
                        </td>
                    </tr>
                </transition>
                <tr v-if="!insertMode && isEditable" @click="insertRow()" class="insert-row">
                    <td :colspan="this.fields.length + 1">
                        <div class="tableBodyCell">
                            <div>
                                <i class="fas fa-plus-circle fa-2x"></i>
                            </div>
                        </div>
                    </td>
                </tr>
            </tbody>
        </table>
    </div>
</template>

<script>
import _ from 'lodash';
import moment from 'moment';
import uuidV4 from 'uuid/v4';
import Upload from '../components/Upload.vue';
import UploadRegular from '../components/UploadRegular.vue';

export default {
    components: {Upload, UploadRegular},
    name: 'CustomTable',
    props: {
        initialData: {
            type: Object,
            default: () => ({
                items: [],
                fields: []
            })
        },
        isEditable: {
            type: Boolean,
            default: () => false
        }
    },
    data() {
        return {
            insertMode: false, // toggles insert mode
            fields: [], // table headers
            items: [], // includes some info to show on cells
            tempItem: {}, // stores data entered by user
            lastColumnWidth: '140px', // 'em' typed width value to specify action column width
            tableFontSize: 0.875,
            tableId: uuidV4(),
            reactiveId: uuidV4()
        };
    },
    filters: {
        idToPaymentMethod(val, paymentMethods) {
            let found = _.find(paymentMethods, {value: val});
            return found ? this.$t(found.text) : '';
        }
    },

    methods: {
        isNil(value) {
            return _.isNil(value);
        },
        // Hide warning message
        resetWarning(item) {
            item.classes['is-invalid'] = false;
            item.hasWarn = false;
        },

        // Make a row editable
        makeRowEditable(item) {
            this.items.forEach(this.revertChanges);
            this.insertMode = false;
            item.editMode = true;

            // Focus on first item in row
            return this.$nextTick().then(() => {
                let elements = document.getElementsByClassName('masked-input-custom');
                elements[0].focus();
                return true;
            });
        },

        // Insert a new table row
        insertRow() {
            this.insertMode = true;
            this.items.forEach(item => {
                item.editMode = false;
            });
            this.bindTempItemToView();

            // Focus on first item in row
            return this.$nextTick().then(() => {
                let elements = document.getElementsByClassName('masked-input-custom');
                elements[0].focus();
                return true;
            });
        },

        // Cancel newly inserted row
        cancelNewRow() {
            this.insertMode = false;
            this.bindTempItemToView();
        },

        // Insert new item
        createItem(item) {
            if (this.isInsertValid(item)) {
                this.$emit('insertItem', item, data => {
                    this.insertMode = false;
                    this.items.push(this.prepareItem(data));
                    this.bindTempItemToView();
                    this.calculateLastColumnWidth();
                });
            }
        },

        // Update existing item
        updateItem(item, fields) {
            if (this.isUpdateValid(item, fields)) {
                this.$emit('applyDataChanges', item, fields, data => {
                    let updatedItem = this.prepareItem(data);
                    for (let key in item) {
                        item[key] = updatedItem[key];
                    }
                    item.editMode = false;
                });
            }
        },

        // Delete an item
        deleteItem($event, id) {
            this.showWarnModal($event.target);
            this.$eventHub.$on(this.$store.state.modal.eventId, () => {
                this.$emit('deleteItemById', id);
                this.items = this.items.filter(item => {
                    return item.id !== id;
                });
            });
        },

        setPaymentMethod(id, item) {
            item.editedValue = id;
        },

        handleUploading(event, item) {
            item.editedFilename = event.filename;
            item.editedFileId = event.fileId;
            item.editedFileUrl = event.fileUrl;
            this.$store.state.uploadFiles = [];
            this.$forceUpdate();
        },

        handleTempUploading(event, item) {
            item.filename = event.filename;
            item.fileId = event.fileId;
            item.fileUrl = event.fileUrl;
        },

        handleRemoving(event, item) {
            item.editedFileId = null;
            this.$forceUpdate();
        },

        handleTempRemoving(event, item) {
            item.fileId = null;
        },

        // Revert any changes made on an item
        revertChanges(item) {
            this.fields.forEach(field => {
                if (field.editable && item[field.key]) {
                    if (field.type === 'date') {
                        item[field.key].editedValue = moment(item[field.key].value).toDate();
                        // TODO: Make this work for currencies other than Turkish
                        // Also put it in its own function
                    } else if (field.type === 'daterange') {
                        item[field.key].editedValue = [
                            moment(item[field.key].value[0]).toDate(),
                            moment(item[field.key].value[1]).toDate()
                        ];
                    } else if (field.type === 'float' || field.type === 'number') {
                        item[field.key].editedValue = this.convertToLocalizedFloat(
                            item[field.key].value,
                            this.locale
                        );
                    } else if (
                        field.type === 'file' &&
                        (item[field.key].editedFileId || !item[field.key].editedFileUrl)
                    ) {
                        item[field.key].editedFileId = item[field.key].fileId;
                    }
                    item[field.key].classes['is-invalid'] = false;
                    item[field.key].hasWarn = false;
                }
            });
            item.editMode = false;
        },

        // Validate newly entered data
        isInsertValid(item) {
            let isValid = true;
            for (let key in item) {
                if (
                    item[key].required &&
                    _.isNil(item[key].value) &&
                    item[key].hasOwnProperty('value')
                ) {
                    item[key].classes['is-invalid'] = true;
                    item[key].hasWarn = true;
                    isValid = false;
                }
            }
            if (!isValid) {
                this.$toasted.error(this.$t('missingError'));
            }
            return isValid;
        },

        // Validate updates to existing data
        isUpdateValid(item, fields) {
            let isValid = true;
            fields.forEach(field => {
                if (field.required && field.editable && field.type !== 'file') {
                    if (!item[field.key].editedValue) {
                        item[field.key].classes['is-invalid'] = true;
                        item[field.key].hasWarn = true;
                        isValid = false;
                    }
                }
            });
            if (!isValid) {
                this.$toasted.error(this.$t('missingError'));
            }
            return isValid;
        },

        // Prepare items in the correct format
        // The property editValue is used to store data entered by user
        prepareItem(row) {
            let generated = {};
            for (let key in row) {
                let foundField = _.find(this.fields, {key: key});
                if (foundField) {
                    generated[key] = {
                        value: row[key]
                    };
                    if (this.isEditable) {
                        if (foundField.type === 'daterange') {
                            generated[key].editedValue = [
                                moment(generated[key].value[0]).toDate(),
                                moment(generated[key].value[1]).toDate()
                            ];
                            // TODO: Make this work for currencies other than Turkish
                            // Also put it in its own function
                        } else if (foundField.type === 'date') {
                            generated[key].editedValue = moment(generated[key].value).toDate();
                        } else if (foundField.type === 'float' || foundField.type === 'number') {
                            if(foundField.type === 'number'){
                                generated[key].value = Number(this.delocalizeValue(_.toString(generated[key].value)).replaceAll('.', '').replaceAll(',', '.'));
                            }
                            generated[key].value = generated[key].value.toFixed(2);
                            generated[key].editedValue = this.convertToLocalizedFloat(
                                generated[key].value,
                                this.locale
                            );
                        } else if (foundField.type === 'link') {
                            generated[key].editedValue = generated[key].value;
                        } else if (foundField.type === 'file' && !_.isEmpty(row[key])) {
                            generated[key].fileId = row[key].fileId ? row[key].fileId : row[key];
                            if (typeof generated[key].fileId === 'object') {
                                generated[key].fileId = _.get(
                                    generated[key].fileId,
                                    'editedFileId'
                                );
                            }
                            if (row[key].editedFileId || !row[key].editedFileUrl) {
                                generated[key].editedFileId = generated[key].fileId;
                            }
                            generated[key].fileUrl = row[key].fileUrl || row[key].editedFileUrl;
                            generated[key].editedFileUrl =
                                generated[key].fileUrl || row[key].editedFileUrl;
                            generated[key].filename = row[key].filename || row[key].editedFilename;
                            generated[key].editedFilename =
                                generated[key].filename || row[key].editedFilename;
                        } else if (foundField.type === 'select') {
                            generated[key].editedValue = generated[key].value;
                        } else {
                            generated[key].editedValue = generated[key].value;
                        }

                        generated[key].classes = {
                            'form-control':
                                foundField.type !== 'date' && foundField.type !== 'daterange',
                            'is-invalid': false
                        };
                        generated[key].hasWarn = false;
                        generated[key].required = foundField.required;
                    } else {
                        if (foundField.type === 'file' && !_.isEmpty(row[key])) {
                            generated[key].fileId = row[key].fileId;
                            generated[key].editedFileId = generated[key].fileId;
                            generated[key].fileUrl = row[key].fileUrl;
                            generated[key].editedFileUrl = generated[key].fileUrl;
                            generated[key].filename = row[key].filename;
                            generated[key].editedFilename = generated[key].filename;
                        }
                    }
                }
            }

            generated.editMode = false;
            generated.id = row.id;
            generated.path = row.path;

            return generated;
        },

        setDefaultDateTypedValue(type, key) {
            let defaultVal = null;
            if (this.items.length) {
                if (type === 'date') {
                    let lastItem = this.items[this.items.length - 1];
                    defaultVal = lastItem[key].value;
                } else if (type === 'daterange') {
                    let lastItem = this.items[this.items.length - 1];
                    defaultVal = lastItem[key].value;
                }
            } else {
                if (type === 'date') {
                    defaultVal = this.createUTCDate(this.year, 0, 1);
                } else if (type === 'daterange') {
                    defaultVal = this.createUTCDate(this.year, 0, 1);
                }
            }
            return defaultVal;
        },

        setDefaultSelectTypedValue(key) {
            let defaultVal = null;
            if (this.items.length) {
                let lastItem = this.items[this.items.length - 1];
                defaultVal = lastItem[key].value;
            }
            return defaultVal;
        },

        // Bind temporary item storage to view
        bindTempItemToView() {
            this.tempItem = {};
            this.fields.forEach(field => {
                if (field.editable) {
                    if (field.type === 'file') {
                        this.$set(this.tempItem, field.key, {
                            filename: null,
                            fileUrl: null,
                            fileId: null
                        });
                    } else {
                        let defaultValue = null;
                        if (field.type === 'date' || field.type === 'daterange') {
                            defaultValue = this.setDefaultDateTypedValue(field.type, field.key);
                        } else if (field.type === 'select') {
                            defaultValue = this.setDefaultSelectTypedValue(field.key);
                        }
                        this.$set(this.tempItem, field.key, {
                            value: field.type === 'select' ? defaultValue : null,
                            defaultValue,
                            classes: {
                                'form-control': field.type !== 'date' && field.type !== 'daterange',
                                'is-invalid': false
                            },
                            hasWarn: false,
                            required: field.required
                        });
                    }
                }
            });
        },

        // Called on initial render and every data update
        init(items = [], fields = []) {
            this.fields = fields;
            this.fields = this.fields.filter(field => !field.header.includes('Imza'));
            this.items = items.map(this.prepareItem);
            if (this.isEditable) {
                this.bindTempItemToView();
            }
            this.calculateLastColumnWidth();
        },

        calculateLastColumnWidth() {
            return this.$nextTick().then(() => {
                let element = document.getElementById('lastColumn');
                if (element) {
                    this.lastColumnWidth = element.offsetWidth + 'px';
                }
                return true;
            });
        },

        routeTo(item) {
            if (!this.isEditable) {
                this.$router.push({path: item.path});
            }
        },

        triggerPopover(item) {
            document.getElementById(`a_${this.reactiveId}_${item.id}`).focus();
        }
    },

    computed: {
        cursorType() {
            var cursorClass = 'cursor-isEditable';
            if (!this.isEditable) {
                cursorClass = 'cursor-nonEditable';
            }
            return cursorClass;
        }
    },

    watch: {
        initialData(newVal = {}) {
            this.init(_.cloneDeep(newVal.items), _.cloneDeep(newVal.fields));
        }
    },

    created() {
        window.addEventListener('resize', this.calculateLastColumnWidth);
        this.init(_.cloneDeep(this.initialData.items), _.cloneDeep(this.initialData.fields));
    },

    beforeDestroy() {
        window.removeEventListener('resize', this.calculateLastColumnWidth);
    }
};
</script>

<style lang="scss" scoped>
.fadere-enter-active {
    opacity: 1;
    transition: opacity 0.6s ease;
}

.fadere-enter,
.fadere-leave-to,
.fadere-leave-active {
    opacity: 0;
}

.cursor-isEditable {
    cursor: default;
}

.cursor-nonEditable {
    cursor: pointer;
}

.file {
    cursor: pointer;
    .file-text {
        &:hover {
            text-decoration: underline;
        }
    }
}
</style>

<i18n src="../i18n/common.json"></i18n>

<i18n>
    {
        "tr": {
            "startDate": "Başlangıç",
            "endDate": "Bitiş",
            "health": "Veri Kontrolü",
            "file": "Fatura",
            "lastUpdatedAt": "Son Değişiklik",
            "date": "Tarih Aralığı",
            "cost": "Tutar",
            "paymentMethod": "Ödeme Tipi",
            "dataType": "Veri Tipi",
            "value": "Tüketim Miktarı"
        },
        "en": {
            "startDate": "Start Date",
            "endDate": "End Date",
            "health": "Data Health",
            "date": "Date Range",
            "file": "Bill",
            "lastUpdatedAt": "Last Update",
            "cost": "Cost",
            "paymentMethod": "Payment Method",
            "dataType": "Data Type",
            "value": "Amount"
        }
    }
</i18n>



// WEBPACK FOOTER //
// src/components/CustomTable.vue